import React from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, makeStyles } from '@material-ui/core'
import COLORS from '../../../utils/constants/colors'
import useRackStyles, { ACCENT, CONDENSED } from './rackStyles'

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px 10px',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    '&:first-child': { borderTop: 'none', paddingTop: 0 },
  },
  label: {
    padding: '0 4px 2px',
    fontFamily: CONDENSED,
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#9aa1aa',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minHeight: '44px',
    boxSizing: 'border-box',
    padding: '0 6px 0 12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
  },
  adRow: {
    border: '1px dashed rgba(255, 255, 255, 0.16)',
    backgroundColor: 'transparent',
  },
  liveRow: {
    border: `1px solid ${ACCENT}`,
    backgroundColor: 'rgba(255, 123, 91, 0.16)',
  },
  removing: { opacity: 0.5 },
  name: {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '14px',
    color: '#f2f3f5',
  },
  tag: {
    flex: 'none',
    padding: '2px 6px',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    borderRadius: '5px',
    fontFamily: CONDENSED,
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#c8ccd2',
  },
  spacer: { flex: 1 },
  playing: {
    flex: 'none',
    padding: '3px 7px',
    borderRadius: '5px',
    background: ACCENT,
    fontFamily: CONDENSED,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#1a0d08',
  },
  iconButton: {
    flex: 'none',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    borderRadius: '8px',
    background: 'transparent',
    color: '#aeb4bc',
    cursor: 'pointer',
    '&:hover': { background: 'rgba(255, 255, 255, 0.08)', color: '#fff' },
  },
  addButton: {
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    height: '32px',
    padding: '0 10px',
    border: '1px solid rgba(255, 255, 255, 0.16)',
    borderRadius: '8px',
    background: 'transparent',
    color: '#e3e6ea',
    fontSize: '13px',
    cursor: 'pointer',
    '&:hover': { background: 'rgba(255, 255, 255, 0.06)' },
    '&:disabled': { color: '#c8ccd2', cursor: 'default' },
  },
  play: {
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    height: '44px',
    border: 'none',
    borderRadius: '10px',
    background: ACCENT,
    color: '#1a0d08',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    '&:disabled': { background: 'rgba(255, 255, 255, 0.08)', color: '#8d939c', cursor: 'default' },
  },
  playStarting: { '&:disabled': { background: ACCENT, opacity: 0.85, color: '#1a0d08' } },
  stopped: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: '40px',
    padding: '0 12px',
    border: `1px solid ${COLORS.red}`,
    borderRadius: '10px',
    backgroundColor: 'rgba(198, 40, 40, 0.16)',
    fontSize: '14px',
    fontWeight: 500,
    color: '#f2f3f5',
  },
  empty: {
    padding: '14px 12px',
    border: '1px dashed rgba(255, 255, 255, 0.14)',
    borderRadius: '10px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#a3a9b1',
  },
})

const Cross = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    aria-hidden='true'
  >
    <path d='M6 6l12 12M18 6L6 18' />
  </svg>
)

const Plus = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    aria-hidden='true'
  >
    <path d='M12 5v14M5 12h14' />
  </svg>
)

const Warning = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M12 3l10 18H2zM12 10v5M12 18h.01' />
  </svg>
)

const PlayIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
    <path d='M8 5v14l11-7z' />
  </svg>
)

// The video on air, or the one the channel is switching to.
const ON_AIR = ['Playing', 'Starting']

/**
 * The event's ad videos: the queue in play order from what is on air, and the library to add from.
 * Mismatch ads in the queue are never named and cannot be removed.
 */
const AdVideoPanel = ({
  videos,
  upNext,
  live,
  adding,
  removing,
  starting,
  stopped,
  onAdd,
  onRemove,
  onStart,
}) => {
  const rack = useRackStyles()
  const classes = useStyles()
  const onAir = upNext.some((item) => ON_AIR.includes(item.state))
  const hasQueued = upNext.some((item) => item.source === 'ClientAd' && item.state === 'Queued')

  const status = (item) => {
    if (item.state === 'Playing') return <span className={classes.playing}>Playing</span>
    if (item.state === 'Starting')
      return (
        <span className={classes.iconButton} role='status' aria-label='Starting'>
          <CircularProgress size={14} color='inherit' />
        </span>
      )
    if (item.state === 'Next') return <span className={classes.tag}>Next</span>
    if (item.state !== 'Queued' || item.source !== 'ClientAd') return null
    if (removing.includes(item.entryId))
      return (
        <span className={classes.iconButton} role='status' aria-label='Removing'>
          <CircularProgress size={14} color='inherit' />
        </span>
      )
    return (
      <button
        type='button'
        className={classes.iconButton}
        onClick={() => onRemove(item.entryId)}
        aria-label={`Remove ${item.filename}`}
      >
        <Cross />
      </button>
    )
  }

  return (
    <div className={rack.list} style={{ padding: 0 }}>
      <div className={classes.section}>
        <div className={classes.label}>Queue</div>
        {stopped && upNext.length > 0 && (
          <div className={classes.stopped} role='alert'>
            <Warning />
            {stopped}
          </div>
        )}
        {upNext.length === 0 ? (
          <div className={classes.empty}>Queue is empty</div>
        ) : (
          upNext.map((item, index) => (
            <div
              // Ads have no id; their place in the list is what tells them apart.
              key={item.entryId ?? `ad-${index}`}
              className={[
                classes.row,
                item.source === 'MismatchAd' && classes.adRow,
                ON_AIR.includes(item.state) && classes.liveRow,
                removing.includes(item.entryId) && classes.removing,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {item.source === 'ClientAd' ? (
                <span className={classes.name}>{item.filename}</span>
              ) : (
                <>
                  <span className={classes.tag}>Ad</span>
                  <span className={classes.spacer} />
                </>
              )}
              {status(item)}
            </div>
          ))
        )}

        {!onAir && (
          <button
            type='button'
            className={`${classes.play} ${starting ? classes.playStarting : ''}`}
            onClick={onStart}
            disabled={starting || !live || !hasQueued}
            aria-busy={starting}
          >
            {starting ? <CircularProgress size={14} color='inherit' /> : live && <PlayIcon />}
            {live ? 'Play' : 'Stream not live'}
          </button>
        )}
      </div>

      <div className={classes.section}>
        <div className={classes.label}>Library</div>
        {videos.map((video) => (
          <div key={video.id} className={classes.row}>
            <span className={classes.name}>{video.filename}</span>
            <button
              type='button'
              className={classes.addButton}
              onClick={() => onAdd(video.id)}
              disabled={adding.includes(video.id)}
              aria-label={`Add ${video.filename} to the queue`}
            >
              {adding.includes(video.id) ? (
                <CircularProgress size={12} color='inherit' />
              ) : (
                <Plus />
              )}
              Add
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

const ITEM_STATES = ['Playing', 'Starting', 'Next', 'Queued']

AdVideoPanel.propTypes = {
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      filename: PropTypes.string.isRequired,
    }),
  ).isRequired,
  upNext: PropTypes.arrayOf(
    PropTypes.shape({
      source: PropTypes.oneOf(['ClientAd', 'MismatchAd']).isRequired,
      entryId: PropTypes.string,
      mediaId: PropTypes.string,
      filename: PropTypes.string,
      state: PropTypes.oneOf(ITEM_STATES).isRequired,
    }),
  ),
  live: PropTypes.bool,
  adding: PropTypes.arrayOf(PropTypes.string),
  removing: PropTypes.arrayOf(PropTypes.string),
  starting: PropTypes.bool,
  stopped: PropTypes.string,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
}

AdVideoPanel.defaultProps = {
  upNext: [],
  live: false,
  adding: [],
  removing: [],
  starting: false,
  stopped: null,
}

export default AdVideoPanel
